import React, { useEffect, useState } from "react";
import cn from "classnames";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import style from "./ExecutorTab.module.scss";
import { Driver } from "../../../../../services";
import UserProfileIcon from "../../../../shared/icons/UserProfileIcon";
import TransportCoveredIconNew from "../../../../shared/icons/TransportCoveredIconNew";
import AssignDriverModal from "../AssignDriverModal/AssignDriverModal";
import PlusIcon from "../../../../shared/icons/PlusIcon";
import EditIconNew from "../../../../shared/icons/EditIconNew";

const ALLOWED_STATUSES = ["assignedexecutor", "booked", "confirmed", "processloadingcargo", "onthetrip"];

const ExecutorTab = ({ order, fetchOrderData, onForceUpdate }) => {
  const { t } = useTranslation();
  const { user } = useSelector((state) => state);

  const [driversData, setDriversData] = useState({});
  const [assignModal, setAssignModal] = useState({ open: false, mode: "add", driverRecordId: null, driverId: null, vehicleId: null, tab: "driver" });

  const drivers = order?.drivers || [];
  const driversKey = drivers.map((d) => d.driverId).join(",");

  useEffect(() => {
    if (!drivers.length) {
      setDriversData({});
      return;
    }

    drivers.forEach(({ driverId, vehicleId }) => {
      setDriversData((prev) => ({
        ...prev,
        [driverId]: prev[driverId] || { info: null, vehicleInfo: null },
      }));

      Driver.getDriverInfo(driverId)
        .then((r) => {
          if (r.status === 200) {
            setDriversData((prev) => ({
              ...prev,
              [driverId]: { ...prev[driverId], info: r.data || {} },
            }));
          }
        })
        .catch(() => {});

      if (vehicleId) {
        Driver.getVehicleInfo(vehicleId)
          .then((r) => {
            if (r.status === 200) {
              setDriversData((prev) => ({
                ...prev,
                [driverId]: { ...prev[driverId], vehicleInfo: r.data || {} },
              }));
            }
          })
          .catch(() => {});
      }
    });
  }, [driversKey]);

  const openEditModal = (driverRecordId, driverId, vehicleId, tab = "driver") =>
    setAssignModal({ open: true, mode: "replace", driverRecordId, driverId, vehicleId, tab });
  const openAddModal = () =>
    setAssignModal({ open: true, mode: "add", driverRecordId: null, driverId: null, vehicleId: null, tab: "driver" });
  const closeAssignModal = () =>
    setAssignModal({ open: false, mode: "add", driverRecordId: null, driverId: null, vehicleId: null, tab: "driver" });

  const handleAssignSuccess = () => {
    if (fetchOrderData) fetchOrderData();
    if (onForceUpdate) onForceUpdate();
  };

  const isOtherExpeditorAssigned = !!order?.expeditorId && order.expeditorId !== user?.companyId;
  const canShowButtons = ALLOWED_STATUSES.includes(order?.status?.toLowerCase()) && !isOtherExpeditorAssigned;
  const hasDrivers = drivers.length > 0;

  if (!hasDrivers && !canShowButtons) {
    return <div className={style.empty}>{t("orderInfo.ordersDriverInfo.noExecutor")}</div>;
  }

  return (
    <div className={style.wrapper}>
      {drivers.map(({ id: driverRecordId, driverId, vehicleId, comment, shipmentTransferDate }) => {
        const { info, vehicleInfo } = driversData[driverId] || { info: null, vehicleInfo: null };
        const driverName = info
          ? [info.surname, info.name, info.patronymic].filter(Boolean).join(" ")
          : "—";

        return (
          <React.Fragment key={driverId}>
            <div>
              {(comment || shipmentTransferDate) && (
                <div className={style.route_bar}>
                  <span className={style.route_text}>{comment || "—"}</span>
                  {shipmentTransferDate && (
                    <span className={style.route_date}>
                      {moment(shipmentTransferDate).format("DD.MM.YYYY")}
                    </span>
                  )}
                </div>
              )}
              
              <div className={cn(style.card, { [style.card_connected]: comment || shipmentTransferDate })}>
                <div className={style.topbar}>
                  <div className={style.avatar}>
                    <UserProfileIcon />
                  </div>
                  <div className={style.topbar_info}>
                    <span className={style.topbar_label}>{t("orderInfo.ordersDriverInfo.driverLabel")}</span>
                    <span className={style.topbar_name}>{driverName}</span>
                  </div>
                  {canShowButtons && (
                    <button className={style.edit_btn} onClick={() => openEditModal(driverRecordId, driverId, vehicleId)}>
                      <EditIconNew width="20" height="20" />
                      <span>{t("confirmDocs.button.replace")}</span>
                    </button>
                  )}
                </div>
                <div className={style.fields_grid}>
                  <div className={style.field}>
                    <span className={style.field_label}>
                      {info?.iin
                        ? t("orderInfoExpeditor.executorTab.iinPassportLabel")
                        : t("orderInfoExpeditor.executorTab.passportNumber")}
                    </span>
                    <span className={style.field_value}>
                      {info?.iin ? `${info.iin} / ${info?.idNumber}` : info?.idNumber || "—"}
                    </span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.phoneLabel")}</span>
                    <span className={style.field_value}>{info?.phoneNumber ? `+${info.phoneNumber}` : "—"}</span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("driversCreatePage.issuedBy.label")}</span>
                    <span className={style.field_value}>{info?.issuedBy || "—"}</span>
                  </div>
                  <div className={style.field}>
                    <span className={style.field_label}>{t("driversCreatePage.issuedDate.label")}</span>
                    <span className={style.field_value}>
                      {info?.issuedDate ? moment(info.issuedDate).format("DD.MM.YYYY") : "—"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className={style.card}>
              <div className={style.topbar}>
                <div className={style.transport_icon}>
                  <TransportCoveredIconNew />
                </div>
                <div className={style.topbar_info}>
                  <span className={style.topbar_label}>{t("orderInfo.ordersDriverInfo.transportLabel")}</span>
                  <span className={style.topbar_name}>{vehicleInfo?.vehicleTypeName || vehicleInfo?.carModel || "—"}</span>
                </div>
                {canShowButtons && (
                  <button className={style.edit_btn} onClick={() => openEditModal(driverRecordId, driverId, vehicleId, "vehicle")}>
                    <EditIconNew width="20" height="20" />
                    <span>{t("confirmDocs.button.replace")}</span>
                  </button>
                )}
              </div>
              <div className={style.fields_grid}>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.carModelLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.carModel || "—"}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.carNumberLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.carNumber || "—"}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.trailerModelLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.trailerModel || "—"}</span>
                </div>
                <div className={style.field}>
                  <span className={style.field_label}>{t("orderInfo.ordersDriverInfo.trailerNumberLabel")}</span>
                  <span className={style.field_value}>{vehicleInfo?.trailerNumber || "—"}</span>
                </div>
              </div>
            </div>

          </React.Fragment>
        );
      })}

      {canShowButtons && (
        <div className={style.buttons}>
          <button className={style.btn} onClick={openAddModal}>
            <PlusIcon width="20" height="20" fill="#0d3b66" />
            <span className={style.btn_text}>{t("orderInfo.orderDecision.addDriverButton")}</span>
          </button>
        </div>
      )}

      <AssignDriverModal
        isOpen={assignModal.open}
        onClose={closeAssignModal}
        order={order}
        mode={assignModal.mode}
        driverRecordId={assignModal.driverRecordId}
        currentDriverId={assignModal.driverId}
        currentVehicleId={assignModal.vehicleId}
        initialTab={assignModal.tab}
        onSuccess={handleAssignSuccess}
      />
    </div>
  );
};

export default ExecutorTab;
